import { Button, Input } from "@qualtio-workhub/ui";
import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type AutocompleteUser,
  filterUsersForAutocomplete,
  getUserDisplayLabel,
} from "./user-autocomplete";

type UserAutocompleteProps = {
  id: string;
  users: readonly AutocompleteUser[];
  selectedUserId: string | null;
  placeholder: string;
  clearLabel: string;
  noResultsLabel: string;
  onSelectedUserIdChange(userId: string | null): void;
};

export function UserAutocomplete({
  id,
  users,
  selectedUserId,
  placeholder,
  clearLabel,
  noResultsLabel,
  onSelectedUserIdChange,
}: UserAutocompleteProps) {
  const generatedId = useId();
  const listboxId = `${id}-${generatedId}-listbox`;
  const selectedUser = useMemo(
    () => users.find((user) => user.id === selectedUserId) ?? null,
    [selectedUserId, users],
  );
  const [inputValue, setInputValue] = useState(() => selectedUser?.name ?? "");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [editing, setEditing] = useState(false);
  const blurTimeoutRef = useRef<number | null>(null);
  const suppressBlurResetRef = useRef(false);
  const clearPendingRef = useRef(false);
  const selectedUserNameRef = useRef(selectedUser?.name ?? "");
  selectedUserNameRef.current = selectedUser?.name ?? "";
  const suggestions = useMemo(
    () => filterUsersForAutocomplete(users, inputValue).slice(0, 8),
    [inputValue, users],
  );

  useEffect(() => {
    setInputValue(selectedUser?.name ?? "");
  }, [selectedUser]);

  useEffect(() => {
    if (clearPendingRef.current && selectedUserId === null) {
      setOpen(false);
      setEditing(false);
      if (inputValue.trim().length > 0) {
        setInputValue("");
      }
      return;
    }

    if (!editing && selectedUserId === null && inputValue.trim().length > 0) {
      setInputValue("");
    }
  }, [editing, inputValue, selectedUserId]);

  const clearBlurTimeout = useCallback(() => {
    if (blurTimeoutRef.current !== null) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearBlurTimeout, [clearBlurTimeout]);

  function selectUser(user: AutocompleteUser) {
    clearBlurTimeout();
    setInputValue(user.name);
    setEditing(false);
    setOpen(false);
    setActiveIndex(0);
    onSelectedUserIdChange(user.id);
  }

  function clearOwner() {
    suppressBlurResetRef.current = true;
    clearPendingRef.current = true;
    selectedUserNameRef.current = "";
    clearBlurTimeout();
    setInputValue("");
    setEditing(false);
    setOpen(false);
    setActiveIndex(0);
    window.setTimeout(() => {
      setInputValue("");
      setEditing(false);
      setOpen(false);
      clearPendingRef.current = false;
    }, 150);
    onSelectedUserIdChange(null);
  }

  function onInputChange(event: ChangeEvent<HTMLInputElement>) {
    const nextValue = event.currentTarget.value;
    setInputValue(nextValue);
    setEditing(true);
    setOpen(true);
    setActiveIndex(0);
    if (nextValue.trim().length === 0 && selectedUserId !== null) {
      onSelectedUserIdChange(null);
    }
  }

  function onInputBlur(_event: FocusEvent<HTMLInputElement>) {
    if (suppressBlurResetRef.current) {
      suppressBlurResetRef.current = false;
      return;
    }

    clearBlurTimeout();
    blurTimeoutRef.current = window.setTimeout(() => {
      setOpen(false);
      setEditing(false);
      setActiveIndex(0);
      setInputValue(selectedUserNameRef.current);
      blurTimeoutRef.current = null;
    }, 100);
  }

  const activeOptionId =
    open && suggestions[activeIndex] ? getOptionId(id, activeIndex) : undefined;

  return (
    <div className="user-autocomplete" data-user-autocomplete="true">
      <div className="user-autocomplete-control">
        <Input
          aria-activedescendant={activeOptionId}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={open}
          autoComplete="off"
          id={id}
          placeholder={placeholder}
          role="combobox"
          value={inputValue}
          onBlur={onInputBlur}
          onChange={onInputChange}
          onFocus={() => {
            setEditing(true);
            setOpen(true);
          }}
          onKeyDown={(event) =>
            handleUserAutocompleteKeyDown(
              event,
              suggestions,
              activeIndex,
              setActiveIndex,
              selectUser,
              () => setOpen(false),
            )
          }
        />
        {selectedUserId !== null || inputValue.trim().length > 0 ? (
          <Button
            aria-label={clearLabel}
            className="user-autocomplete-clear"
            size="sm"
            type="button"
            variant="ghost"
            onPointerDown={(event) => {
              suppressBlurResetRef.current = true;
              event.preventDefault();
            }}
            onMouseDown={(event) => {
              suppressBlurResetRef.current = true;
              event.preventDefault();
            }}
            onClick={clearOwner}
          >
            ×
          </Button>
        ) : null}
      </div>
      {selectedUser ? (
        <span className="user-autocomplete-selected">{getUserDisplayLabel(selectedUser)}</span>
      ) : null}
      {open ? (
        <UserSuggestionList
          activeIndex={activeIndex}
          emptyLabel={noResultsLabel}
          id={listboxId}
          inputId={id}
          suggestions={suggestions}
          onSelect={selectUser}
        />
      ) : null}
    </div>
  );
}

function handleUserAutocompleteKeyDown(
  event: KeyboardEvent<HTMLInputElement>,
  suggestions: readonly AutocompleteUser[],
  activeIndex: number,
  setActiveIndex: (index: number) => void,
  selectUser: (user: AutocompleteUser) => void,
  close: () => void,
) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    setActiveIndex(nextIndex(activeIndex, suggestions.length, 1));
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    setActiveIndex(nextIndex(activeIndex, suggestions.length, -1));
  } else if (event.key === "Enter" && suggestions[activeIndex] !== undefined) {
    event.preventDefault();
    selectUser(suggestions[activeIndex]);
  } else if (event.key === "Escape") {
    event.preventDefault();
    close();
  }
}

function UserSuggestionList({
  activeIndex,
  emptyLabel,
  id,
  inputId,
  suggestions,
  onSelect,
}: {
  activeIndex: number;
  emptyLabel: string;
  id: string;
  inputId: string;
  suggestions: readonly AutocompleteUser[];
  onSelect(user: AutocompleteUser): void;
}) {
  return (
    <div className="user-autocomplete-list" id={id} role="listbox">
      {suggestions.length === 0 ? (
        <div className="user-autocomplete-empty">{emptyLabel}</div>
      ) : null}
      {suggestions.map((user, index) => (
        <button
          aria-selected={index === activeIndex}
          className="user-autocomplete-option"
          id={getOptionId(inputId, index)}
          key={user.id}
          role="option"
          type="button"
          onMouseDown={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
          onClick={() => onSelect(user)}
        >
          <strong>{user.name}</strong>
          <span>{user.email}</span>
        </button>
      ))}
    </div>
  );
}

function getOptionId(inputId: string, index: number): string {
  return `${inputId}-option-${index}`;
}

function nextIndex(current: number, length: number, direction: 1 | -1): number {
  if (length === 0) return 0;
  return (current + direction + length) % length;
}
